import { useState, useEffect } from 'react';

interface PaymentMethod {
  name: string;
  url: string;
  enabled: boolean;
}

interface DailyTask {
  day: string;
  videoUrl: string;
  enabled: boolean;
}

interface AppPremium {
  id: number;
  name: string;
  description: string;
  link: string;
  icon: string;
  enabled: boolean;
}

const DEFAULT_DASHBOARD_VIDEO = 'https://m.youtube.com/@LudovicSalenne';

const AVAILABLE_ICONS = [
  { value: 'ri-telegram-line', label: 'Telegram' },
  { value: 'ri-whatsapp-line', label: 'WhatsApp' },
  { value: 'ri-instagram-line', label: 'Instagram' },
  { value: 'ri-youtube-line', label: 'YouTube' },
  { value: 'ri-music-line', label: 'TikTok / Musique' },
  { value: 'ri-spotify-line', label: 'Spotify' },
  { value: 'ri-netflix-line', label: 'Netflix' },
  { value: 'ri-tv-line', label: 'TV / Canal+' },
  { value: 'ri-smartphone-line', label: 'Smartphone' },
  { value: 'ri-android-line', label: 'Android' },
  { value: 'ri-apple-line', label: 'Apple' },
  { value: 'ri-game-line', label: 'Jeu' },
  { value: 'ri-facebook-line', label: 'Facebook' },
  { value: 'ri-twitter-line', label: 'Twitter/X' },
  { value: 'ri-linkedin-line', label: 'LinkedIn' },
  { value: 'ri-discord-line', label: 'Discord' },
];

const ICON_COLORS: Record<string, string> = {
  'ri-telegram-line': 'from-teal-500 to-teal-600',
  'ri-whatsapp-line': 'from-green-500 to-green-600',
  'ri-instagram-line': 'from-pink-500 to-purple-600',
  'ri-youtube-line': 'from-red-500 to-red-600',
  'ri-music-line': 'from-gray-800 to-gray-900',
  'ri-spotify-line': 'from-green-600 to-green-700',
  'ri-netflix-line': 'from-red-600 to-red-700',
  'ri-tv-line': 'from-orange-500 to-orange-600',
  'ri-smartphone-line': 'from-indigo-500 to-indigo-600',
  'ri-android-line': 'from-lime-500 to-lime-600',
  'ri-apple-line': 'from-gray-600 to-gray-700',
  'ri-game-line': 'from-violet-500 to-violet-600',
  'ri-facebook-line': 'from-blue-600 to-blue-700',
  'ri-twitter-line': 'from-sky-500 to-sky-600',
  'ri-linkedin-line': 'from-blue-700 to-blue-800',
  'ri-discord-line': 'from-indigo-600 to-indigo-700',
};

const DEFAULT_APPS: AppPremium[] = [
  { id: 1, name: 'Telegram Premium', description: 'Version premium avec fonctionnalités avancées', link: 'https://t.me/+RLed8IfVgMINDg0', icon: 'ri-telegram-line', enabled: true },
  { id: 2, name: 'WhatsApp Plus', description: 'WhatsApp avec options supplémentaires', link: 'https://whatsapp.com/channel/0029VbCEuEA1NCrMAsPILy3J', icon: 'ri-whatsapp-line', enabled: true },
  { id: 3, name: 'Instagram Pro', description: 'Téléchargez photos et vidéos facilement', link: '', icon: 'ri-instagram-line', enabled: true },
  { id: 4, name: 'YouTube Premium', description: 'Sans publicités et téléchargements', link: '', icon: 'ri-youtube-line', enabled: true },
  { id: 5, name: 'TikTok Pro', description: 'Téléchargez vidéos sans filigrane', link: '', icon: 'ri-music-line', enabled: true },
  { id: 6, name: 'Spotify Premium', description: 'Musique illimitée sans pub', link: '', icon: 'ri-spotify-line', enabled: true },
  { id: 7, name: 'Netflix Premium', description: 'Accès illimité aux films et séries', link: '', icon: 'ri-netflix-line', enabled: true },
  { id: 8, name: 'Canal+ Premium', description: 'Toutes les chaînes Canal+ en direct', link: '', icon: 'ri-tv-line', enabled: true },
];

export default function SiteSettings() {
  const [activeTab, setActiveTab] = useState<'payment' | 'tasks' | 'video' | 'apps' | 'messages'>('payment');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [dailyTasks, setDailyTasks] = useState<DailyTask[]>([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [taskReward, setTaskReward] = useState(150);

  // Vidéo tableau de bord
  const [dashboardVideoUrl, setDashboardVideoUrl] = useState(DEFAULT_DASHBOARD_VIDEO);
  const [videoPreview, setVideoPreview] = useState(false);

  // ✅ Configuration BKaPay
  const [bkapayApiKey] = useState('pk_live_0838efd9-0bb6-47bb-b611-59f72dd3c290');
  const [bkapayAutoActivation, setBkapayAutoActivation] = useState(true);

  // Applications Premium
  const [apps, setApps] = useState<AppPremium[]>([]);
  const [editingApp, setEditingApp] = useState<AppPremium | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newApp, setNewApp] = useState<Omit<AppPremium, 'id'>>({
    name: '', description: '', link: '', icon: 'ri-smartphone-line', enabled: true,
  });

  // Messages personnalisés
  const [redirectTitle, setRedirectTitle] = useState('🔗 Page de paiement ouverte');
  const [redirectBody, setRedirectBody] = useState(
    'Effectuez votre paiement de 3 500 FCFA, puis revenez ici et cliquez sur "J\'ai payé".'
  );
  const [redirectNote, setRedirectNote] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    const savedMethods = localStorage.getItem('paymentMethods');
    if (savedMethods) {
      try { setPaymentMethods(JSON.parse(savedMethods)); }
      catch { setPaymentMethods(getDefaultPaymentMethods()); }
    } else {
      setPaymentMethods(getDefaultPaymentMethods());
    }

    const savedTasks = localStorage.getItem('adminDayContents') || localStorage.getItem('dailyTasks');
    if (savedTasks) {
      try { setDailyTasks(JSON.parse(savedTasks)); }
      catch { setDailyTasks(getDefaultDailyTasks()); }
    } else {
      setDailyTasks(getDefaultDailyTasks());
    }

    // Charger le montant de récompense des tâches
    const savedTaskReward = localStorage.getItem('taskReward');
    if (savedTaskReward) {
      const parsed = parseInt(savedTaskReward);
      if (!isNaN(parsed) && parsed > 0) setTaskReward(parsed);
    }

    const savedAutoActivation = localStorage.getItem('bkapay_auto_activation');
    if (savedAutoActivation !== null) setBkapayAutoActivation(savedAutoActivation === 'true');
    localStorage.setItem('bkapay_api_key', 'pk_live_0838efd9-0bb6-47bb-b611-59f72dd3c290');

    const savedVideo = localStorage.getItem('dashboardVideoUrl');
    if (savedVideo) setDashboardVideoUrl(savedVideo);

    const savedApps = localStorage.getItem('adminApps');
    if (savedApps) {
      try {
        const parsed = JSON.parse(savedApps);
        setApps(parsed.map((a: AppPremium) => ({ ...a, enabled: a.enabled !== false })));
      } catch { setApps(DEFAULT_APPS); }
    } else {
      setApps(DEFAULT_APPS);
    }

    const savedRedirectTitle = localStorage.getItem('payment_redirect_title');
    if (savedRedirectTitle) setRedirectTitle(savedRedirectTitle);
    const savedRedirectBody = localStorage.getItem('payment_redirect_body');
    if (savedRedirectBody) setRedirectBody(savedRedirectBody);
    const savedRedirectNote = localStorage.getItem('payment_redirect_note');
    if (savedRedirectNote) setRedirectNote(savedRedirectNote);
  };

  const getDefaultPaymentMethods = (): PaymentMethod[] => [
    { name: 'BKaPay', url: 'https://bkapay.com', enabled: true },
    { name: 'Soleaspay', url: 'https://soleaspay.com', enabled: true },
    { name: 'Money Fusion', url: 'https://moneyfusion.com', enabled: true },
    { name: 'USDT BEP20', url: '', enabled: true },
    { name: 'Mobile Money Ghana', url: '', enabled: true },
  ];

  const getDefaultDailyTasks = (): DailyTask[] => [
    { day: 'Lundi', videoUrl: '', enabled: true },
    { day: 'Mardi', videoUrl: '', enabled: true },
    { day: 'Mercredi', videoUrl: '', enabled: true },
    { day: 'Jeudi', videoUrl: '', enabled: true },
    { day: 'Vendredi', videoUrl: '', enabled: true },
    { day: 'Samedi', videoUrl: '', enabled: true },
    { day: 'Dimanche', videoUrl: '', enabled: true },
  ];

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSavePaymentMethods = () => {
    try {
      localStorage.setItem('paymentMethods', JSON.stringify(paymentMethods));
      showSuccess('✅ Méthodes de paiement enregistrées avec succès !');
    } catch { showSuccess('⚠️ Erreur lors de l\'enregistrement.'); }
  };

  const handleSaveDailyTasks = () => {
    try {
      localStorage.setItem('adminDayContents', JSON.stringify(dailyTasks));
      localStorage.setItem('dailyTasks', JSON.stringify(dailyTasks));
      // Sauvegarder aussi le montant de récompense
      if (taskReward > 0) {
        localStorage.setItem('taskReward', taskReward.toString());
      }
      showSuccess('✅ Tâches quotidiennes enregistrées avec succès !');
    } catch { showSuccess('⚠️ Erreur lors de l\'enregistrement.'); }
  };

  const handleSaveBKaPayConfig = () => {
    try {
      localStorage.setItem('bkapay_api_key', 'pk_live_0838efd9-0bb6-47bb-b611-59f72dd3c290');
      localStorage.setItem('bkapay_auto_activation', bkapayAutoActivation.toString());
      showSuccess('✅ Configuration BKaPay enregistrée avec succès !');
    } catch { showSuccess('⚠️ Erreur lors de l\'enregistrement.'); }
  };

  const handleSaveDashboardVideo = () => {
    try {
      const url = dashboardVideoUrl.trim() || DEFAULT_DASHBOARD_VIDEO;
      localStorage.setItem('dashboardVideoUrl', url);
      setDashboardVideoUrl(url);
      setVideoPreview(false);
      showSuccess('✅ Lien vidéo du tableau de bord enregistré !');
    } catch { showSuccess('⚠️ Erreur lors de l\'enregistrement.'); }
  };

  const handleSaveMessages = () => {
    try {
      localStorage.setItem('payment_redirect_title', redirectTitle.trim() || '🔗 Page de paiement ouverte');
      localStorage.setItem('payment_redirect_body', redirectBody.trim() || 'Effectuez votre paiement, puis revenez ici.');
      localStorage.setItem('payment_redirect_note', redirectNote.trim());
      showSuccess('✅ Messages personnalisés enregistrés avec succès !');
    } catch {
      showSuccess('⚠️ Erreur lors de l\'enregistrement.');
    }
  };

  // ===== APPS PREMIUM =====
  const handleSaveApps = (updatedApps: AppPremium[]) => {
    try {
      localStorage.setItem('adminApps', JSON.stringify(updatedApps));
      setApps(updatedApps);
      showSuccess('✅ Applications Premium enregistrées avec succès !');
    } catch { showSuccess('⚠️ Erreur lors de l\'enregistrement.'); }
  };

  const handleToggleApp = (id: number) => {
    const updated = apps.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a);
    handleSaveApps(updated);
  };

  const handleUpdateApp = (app: AppPremium) => {
    const updated = apps.map(a => a.id === app.id ? app : a);
    handleSaveApps(updated);
    setEditingApp(null);
  };

  const handleDeleteApp = (id: number) => {
    const updated = apps.filter(a => a.id !== id);
    handleSaveApps(updated);
  };

  const handleAddApp = () => {
    if (!newApp.name.trim()) return;
    const maxId = apps.length > 0 ? Math.max(...apps.map(a => a.id)) : 0;
    const updated = [...apps, { ...newApp, id: maxId + 1 }];
    handleSaveApps(updated);
    setNewApp({ name: '', description: '', link: '', icon: 'ri-smartphone-line', enabled: true });
    setShowAddForm(false);
  };

  const getYoutubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;
    const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return `https://www.youtube.com/embed/${embedMatch[1]}`;
    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
    return null;
  };

  const updatePaymentMethod = (index: number, field: keyof PaymentMethod, value: string | boolean) => {
    const updated = [...paymentMethods];
    updated[index] = { ...updated[index], [field]: value };
    setPaymentMethods(updated);
  };

  const updateDailyTask = (index: number, field: keyof DailyTask, value: string | boolean) => {
    const updated = [...dailyTasks];
    updated[index] = { ...updated[index], [field]: value };
    setDailyTasks(updated);
  };

  const embedPreview = getYoutubeEmbedUrl(dashboardVideoUrl);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Paramètres du site</h2>
        <p className="text-sm text-gray-500">Configurez les méthodes de paiement, les tâches, la vidéo et les applications premium</p>
      </div>

      {successMsg && (
        <div className="mb-4 px-4 py-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2 font-semibold text-sm">
          <i className="ri-checkbox-circle-fill text-lg"></i> {successMsg}
        </div>
      )}

      {/* Onglets */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        {[
          { key: 'payment', icon: 'ri-bank-card-line', label: 'Paiements' },
          { key: 'tasks', icon: 'ri-task-line', label: 'Tâches' },
          { key: 'video', icon: 'ri-youtube-line', label: 'Vidéo' },
          { key: 'apps', icon: 'ri-smartphone-line', label: 'Apps Premium' },
          { key: 'messages', icon: 'ri-message-2-line', label: 'Messages' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`px-4 py-2 font-semibold text-sm transition cursor-pointer whitespace-nowrap ${
              activeTab === tab.key ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${tab.icon} mr-2`}></i>{tab.label}
          </button>
        ))}
      </div>

      {/* ===== ONGLET MESSAGES ===== */}
      {activeTab === 'messages' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                <i className="ri-message-2-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold">Messages personnalisés</h3>
                <p className="text-teal-100 text-sm">Modifiez les textes affichés à l'utilisateur après la redirection vers la page de paiement</p>
              </div>
            </div>
          </div>

          {/* Prévisualisation */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex items-center gap-2">
              <i className="ri-eye-line text-gray-500"></i>
              <span className="text-sm font-bold text-gray-700">Prévisualisation — Écran après redirection</span>
            </div>
            <div className="p-6">
              <div className="max-w-sm mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-full mx-auto mb-1 flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-white font-bold text-sm">SUCCESSLINK AGENCY</p>
                </div>
                <div className="p-5 space-y-4">
                  <div className="text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
                      <i className="ri-external-link-line text-2xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-base font-bold text-gray-800 mb-2">{redirectTitle || '🔗 Page de paiement ouverte'}</h3>
                    <p className="text-xs text-gray-500">{redirectBody || 'Effectuez votre paiement, puis revenez ici.'}</p>
                  </div>
                  {redirectNote && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 font-medium text-center">
                      {redirectNote}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="py-2 px-3 bg-gray-100 text-gray-600 font-semibold rounded-xl text-xs text-center">← Changer</div>
                    <div className="py-2 px-3 bg-emerald-600 text-white font-bold rounded-xl text-xs text-center">J'ai payé ✓</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire d'édition */}
          <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-6 space-y-5">
            <h4 className="font-bold text-gray-800 flex items-center gap-2">
              <i className="ri-edit-line text-teal-600"></i> Modifier les messages
            </h4>

            {/* Titre */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                Titre principal <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={redirectTitle}
                onChange={e => setRedirectTitle(e.target.value)}
                placeholder="Ex: 🔗 Page de paiement ouverte"
                maxLength={100}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition"
              />
              <p className="text-xs text-gray-400 mt-1">{redirectTitle.length}/100 caractères</p>
            </div>

            {/* Corps du message */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                Message principal <span className="text-red-500">*</span>
              </label>
              <textarea
                value={redirectBody}
                onChange={e => setRedirectBody(e.target.value)}
                placeholder="Ex: Effectuez votre paiement, puis revenez ici et cliquez sur J'ai payé."
                maxLength={300}
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{redirectBody.length}/300 caractères</p>
            </div>

            {/* Note supplémentaire */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                Note supplémentaire <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="text"
                value={redirectNote}
                onChange={e => setRedirectNote(e.target.value)}
                placeholder="Ex: ⚠️ Conservez votre reçu de paiement"
                maxLength={150}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none transition"
              />
              <p className="text-xs text-gray-400 mt-1">Affiché dans un encadré jaune sous le message principal. {redirectNote.length}/150</p>
            </div>

            {/* Bouton reset */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setRedirectTitle('🔗 Page de paiement ouverte');
                  setRedirectBody('Effectuez votre paiement de 3 500 FCFA, puis revenez ici et cliquez sur "J\'ai payé".');
                  setRedirectNote('');
                }}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-refresh-line"></i> Réinitialiser
              </button>
              <button
                onClick={handleSaveMessages}
                className="flex-1 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow"
              >
                <i className="ri-save-line"></i> Enregistrer les messages
              </button>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-xs font-bold text-teal-800 mb-1 flex items-center gap-1">
              <i className="ri-information-line"></i> Comment ça fonctionne
            </p>
            <ul className="space-y-1 text-xs text-teal-700">
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Le message s'affiche dès que l'utilisateur clique sur "Payer" et est redirigé</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> La note supplémentaire apparaît dans un encadré jaune si elle est renseignée</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Les modifications sont visibles immédiatement pour tous les utilisateurs</li>
            </ul>
          </div>
        </div>
      )}

      {/* ===== ONGLET APPS PREMIUM ===== */}
      {activeTab === 'apps' && (
        <div className="space-y-5">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-teal-600 rounded-xl p-5 text-white flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold mb-1">Applications Premium</h3>
              <p className="text-indigo-100 text-sm">{apps.filter(a => a.enabled).length} application(s) active(s) sur {apps.length}</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold px-4 py-2 rounded-xl text-sm transition cursor-pointer whitespace-nowrap"
            >
              <i className={showAddForm ? 'ri-close-line' : 'ri-add-line'}></i>
              {showAddForm ? 'Annuler' : 'Ajouter une app'}
            </button>
          </div>

          {/* Formulaire d'ajout */}
          {showAddForm && (
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-5">
              <h4 className="font-bold text-indigo-800 mb-4 flex items-center gap-2">
                <i className="ri-add-circle-line"></i> Nouvelle application
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1">Nom de l'application *</label>
                  <input
                    type="text"
                    value={newApp.name}
                    onChange={e => setNewApp({ ...newApp, name: e.target.value })}
                    placeholder="Ex: Netflix Premium"
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1">Icône</label>
                  <select
                    value={newApp.icon}
                    onChange={e => setNewApp({ ...newApp, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  >
                    {AVAILABLE_ICONS.map(ic => (
                      <option key={ic.value} value={ic.value}>{ic.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-600 mb-1">Description</label>
                  <input
                    type="text"
                    value={newApp.description}
                    onChange={e => setNewApp({ ...newApp, description: e.target.value })}
                    placeholder="Courte description de l'application"
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-600 mb-1">Lien / URL *</label>
                  <input
                    type="url"
                    value={newApp.link}
                    onChange={e => setNewApp({ ...newApp, link: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleAddApp}
                  disabled={!newApp.name.trim()}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-save-line"></i> Ajouter l'application
                </button>
              </div>
            </div>
          )}

          {/* Liste des apps */}
          <div className="space-y-3">
            {apps.map(app => {
              const gradient = ICON_COLORS[app.icon] || 'from-teal-500 to-teal-600';
              const isEditing = editingApp?.id === app.id;
              return (
                <div key={app.id} className={`bg-white rounded-xl border-2 ${app.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'} shadow-sm overflow-hidden`}>
                  {/* Vue normale */}
                  {!isEditing ? (
                    <div className="flex items-center gap-4 p-4">
                      <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${gradient} rounded-xl flex-shrink-0`}>
                        <i className={`${app.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-gray-900 text-sm">{app.name}</span>
                          {!app.enabled && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Désactivé</span>}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{app.description}</p>
                        {app.link ? (
                          <p className="text-xs text-teal-600 font-mono truncate mt-0.5">{app.link}</p>
                        ) : (
                          <p className="text-xs text-orange-400 mt-0.5 flex items-center gap-1"><i className="ri-alert-line"></i> Aucun lien configuré</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Toggle actif/inactif */}
                        <button
                          onClick={() => handleToggleApp(app.id)}
                          className={`relative w-11 h-6 rounded-full transition cursor-pointer ${app.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                          title={app.enabled ? 'Désactiver' : 'Activer'}
                        >
                          <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow ${app.enabled ? 'translate-x-5' : 'translate-x-0'}`}></span>
                        </button>
                        {/* Modifier */}
                        <button
                          onClick={() => setEditingApp({ ...app })}
                          className="w-8 h-8 flex items-center justify-center bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition cursor-pointer"
                          title="Modifier"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        {/* Supprimer */}
                        <button
                          onClick={() => handleDeleteApp(app.id)}
                          className="w-8 h-8 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition cursor-pointer"
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Mode édition */
                    <div className="p-4 bg-indigo-50">
                      <p className="text-xs font-bold text-indigo-700 mb-3 flex items-center gap-1">
                        <i className="ri-edit-line"></i> Modifier : {app.name}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Nom</label>
                          <input
                            type="text"
                            value={editingApp!.name}
                            onChange={e => setEditingApp({ ...editingApp!, name: e.target.value })}
                            className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Icône</label>
                          <select
                            value={editingApp!.icon}
                            onChange={e => setEditingApp({ ...editingApp!, icon: e.target.value })}
                            className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                          >
                            {AVAILABLE_ICONS.map(ic => (
                              <option key={ic.value} value={ic.value}>{ic.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            value={editingApp!.description}
                            onChange={e => setEditingApp({ ...editingApp!, description: e.target.value })}
                            className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-gray-600 mb-1">Lien / URL</label>
                          <input
                            type="url"
                            value={editingApp!.link}
                            onChange={e => setEditingApp({ ...editingApp!, link: e.target.value })}
                            placeholder="https://..."
                            className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-3">
                        <button
                          onClick={() => handleUpdateApp(editingApp!)}
                          className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        >
                          <i className="ri-save-line"></i> Enregistrer
                        </button>
                        <button
                          onClick={() => setEditingApp(null)}
                          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg text-sm transition cursor-pointer whitespace-nowrap"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {apps.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <i className="ri-smartphone-line text-5xl text-gray-200 mb-3"></i>
              <p className="text-gray-500 font-semibold">Aucune application configurée</p>
              <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter une app" pour commencer</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-bold text-blue-800 mb-1 flex items-center gap-1">
              <i className="ri-information-line"></i> Comment ça fonctionne
            </p>
            <ul className="space-y-1 text-xs text-blue-700">
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Activez/désactivez chaque app avec le bouton toggle</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Modifiez le nom, la description, l'icône et le lien de chaque app</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Les changements sont visibles immédiatement sur le tableau de bord des utilisateurs</li>
              <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-fill mt-0.5"></i> Si aucun lien n'est renseigné, le bouton affichera "Bientôt disponible"</li>
            </ul>
          </div>
        </div>
      )}

      {/* ===== ONGLET VIDÉO TABLEAU DE BORD ===== */}
      {activeTab === 'video' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg">
                <i className="ri-youtube-line text-xl text-red-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Vidéo affichée sur le tableau de bord</h3>
                <p className="text-sm text-gray-500">Ce lien s'affiche en haut du tableau de bord de chaque utilisateur</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-links-line text-red-500 mr-1"></i>
                  Lien YouTube (vidéo, chaîne ou shorts)
                </label>
                <input
                  type="url"
                  value={dashboardVideoUrl}
                  onChange={e => { setDashboardVideoUrl(e.target.value); setVideoPreview(false); }}
                  placeholder="https://www.youtube.com/watch?v=... ou https://www.youtube.com/@..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setVideoPreview(!videoPreview)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className={videoPreview ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  {videoPreview ? 'Masquer' : 'Prévisualiser'}
                </button>
                <button
                  onClick={handleSaveDashboardVideo}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-save-line"></i> Enregistrer
                </button>
              </div>
              {videoPreview && (
                <div className="rounded-xl overflow-hidden border-2 border-red-200 bg-gray-50">
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-100">
                    <i className="ri-eye-line text-red-500"></i>
                    <span className="text-xs font-semibold text-red-700">Prévisualisation</span>
                  </div>
                  {embedPreview ? (
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        src={embedPreview}
                        title="Prévisualisation vidéo"
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ border: 'none' }}
                      ></iframe>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4">
                      <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mb-3">
                        <i className="ri-youtube-line text-3xl text-red-500"></i>
                      </div>
                      <p className="font-semibold text-gray-700 text-sm mb-1">Lien de chaîne YouTube détecté</p>
                      <a href={dashboardVideoUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition cursor-pointer whitespace-nowrap">
                        <i className="ri-youtube-line"></i> Voir la chaîne YouTube
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== ONGLET PAIEMENT ===== */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg">
                <i className="ri-shield-check-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Configuration BKaPay</h3>
                <p className="text-sm text-gray-600">Intégration automatique — Redirect Checkout v1.4</p>
              </div>
              <div className="ml-auto">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full border border-emerald-300">
                  <i className="ri-checkbox-circle-fill"></i> Configuré
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white border border-emerald-200 rounded-lg p-4">
                <p className="text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                  <i className="ri-key-line text-emerald-600"></i> Clé publique BKaPay
                </p>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded flex-1 break-all">{bkapayApiKey}</code>
                </div>
              </div>
              <div className="bg-white border border-emerald-200 rounded-lg p-4">
                <p className="text-xs font-bold text-gray-500 mb-1 flex items-center gap-1">
                  <i className="ri-webhook-line text-emerald-600"></i> URL Webhook
                </p>
                <code className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded block break-all">
                  https://successlinkk-agencia.readdy.co/api/webhook/bkapay
                </code>
              </div>
              <div className="bg-white border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                      <i className="ri-flashlight-line text-emerald-600"></i>
                      Activation automatique BKaPay
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {bkapayAutoActivation
                        ? '✅ Les paiements BKaPay confirmés activent automatiquement les comptes'
                        : '⚠️ Les paiements BKaPay nécessitent une validation manuelle'}
                    </p>
                  </div>
                  <button
                    onClick={() => setBkapayAutoActivation(!bkapayAutoActivation)}
                    className={`relative w-14 h-7 rounded-full transition cursor-pointer flex-shrink-0 ${bkapayAutoActivation ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${bkapayAutoActivation ? 'translate-x-7' : 'translate-x-0'}`}></span>
                  </button>
                </div>
              </div>
              <button
                onClick={handleSaveBKaPayConfig}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-save-line text-lg"></i>
                Enregistrer la configuration BKaPay
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
              <i className="ri-bank-card-line text-red-600"></i>
              Méthodes de paiement disponibles
            </h3>
            <div className="space-y-4">
              {paymentMethods.map((method, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg">
                        <i className="ri-money-dollar-circle-line text-red-600"></i>
                      </div>
                      <span className="font-semibold text-gray-900">{method.name}</span>
                    </div>
                    <button
                      onClick={() => updatePaymentMethod(index, 'enabled', !method.enabled)}
                      className={`relative w-12 h-6 rounded-full transition cursor-pointer ${method.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${method.enabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={method.url}
                    onChange={(e) => updatePaymentMethod(index, 'url', e.target.value)}
                    placeholder={`URL pour ${method.name}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={handleSavePaymentMethods}
              className="w-full mt-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-save-line text-lg"></i>
              Enregistrer les méthodes de paiement
            </button>
          </div>
        </div>
      )}

      {/* ===== ONGLET TÂCHES ===== */}
      {activeTab === 'tasks' && (
        <div className="bg-white rounded-xl shadow border border-gray-200 p-6">
          <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
            <i className="ri-task-line text-red-600"></i>
            Configuration des tâches quotidiennes
          </h3>

          {/* Montant de récompense */}
          <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Récompense par tâche</h4>
                <p className="text-xs text-teal-700">Montant crédité à l'utilisateur après chaque tâche accomplie</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide">
                  Montant (F) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={taskReward}
                  onChange={e => setTaskReward(parseInt(e.target.value) || 150)}
                  min="1"
                  step="1"
                  className="w-full px-4 py-3 border-2 border-teal-300 rounded-xl text-sm font-bold text-teal-800 focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none bg-white"
                />
              </div>
              <div className="bg-white border-2 border-teal-300 rounded-xl px-5 py-3 text-center flex-shrink-0">
                <p className="text-xs text-teal-600 font-semibold">Actuel</p>
                <p className="text-2xl font-extrabold text-teal-700">{taskReward} F</p>
              </div>
            </div>
            <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
              <i className="ri-information-line"></i>
              Valeur actuelle : <strong>{taskReward} F</strong> par tâche. Modifiez et enregistrez pour mettre à jour.
            </p>
          </div>

          <div className="space-y-4">
            {dailyTasks.map((task, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg">
                      <i className="ri-calendar-line text-red-600"></i>
                    </div>
                    <span className="font-semibold text-gray-900">{task.day}</span>
                  </div>
                  <button
                    onClick={() => updateDailyTask(index, 'enabled', !task.enabled)}
                    className={`relative w-12 h-6 rounded-full transition cursor-pointer ${task.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${task.enabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
                  </button>
                </div>
                <input
                  type="text"
                  value={task.videoUrl}
                  onChange={(e) => updateDailyTask(index, 'videoUrl', e.target.value)}
                  placeholder={`URL de la vidéo pour ${task.day}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-400 outline-none"
                />
              </div>
            ))}
          </div>
          <button
            onClick={handleSaveDailyTasks}
            className="w-full mt-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-save-line text-lg"></i>
            Enregistrer les tâches et la récompense
          </button>
        </div>
      )}
    </div>
  );
}
